import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { readResponse } from '../utils/fetchSafe';

const PrintTimeCards = () => {
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [calFromOpen, setCalFromOpen] = useState(false);
  const [calToOpen, setCalToOpen] = useState(false);
  const [groupedData, setGroupedData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePrint = async () => {
    if (!timeFrom || !timeTo) {
      toast.error('Please select both dates');
      return;
    }
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/timeslips?date_from=${encodeURIComponent(timeFrom)}&date_to=${encodeURIComponent(timeTo)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const { data: body } = await readResponse(res);
          if (body?.detail) detail = body.detail;
        } catch {
          try { detail = (await res.text()).slice(0, 200) || detail; } catch {}
        }
        throw new Error(detail);
      }
      const { data } = await readResponse(res);
      const slips = data.timeslips || [];
      if (slips.length === 0) {
        toast.info('No time entries found for the selected range');
        setGroupedData(null);
        return;
      }
      // Group by employee
      const grouped = {};
      for (const slip of slips) {
        const emp = slip.employee || 'Unknown';
        if (!grouped[emp]) grouped[emp] = [];
        grouped[emp].push(slip);
      }
      setGroupedData(grouped);
    } catch (e) {
      console.error('Time cards fetch failed:', e);
      toast.error(`Failed to fetch time cards: ${e.message || 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBrowserPrint = () => {
    const printContent = document.querySelector('[data-testid="print-view"]');
    if (!printContent) return;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      window.print();
      return;
    }
    printWindow.document.write(`
      <html><head><title>Time Cards: ${timeFrom} to ${timeTo}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 2rem; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { text-align: left; padding: 6px 8px; font-weight: 600; color: #555; font-size: 12px; border-bottom: 2px solid #ccc; }
        td { padding: 6px 8px; color: #333; font-size: 12px; border-bottom: 1px solid #ddd; }
        .text-right { text-align: right; }
        .mb-12 { margin-bottom: 3rem; page-break-inside: avoid; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-4 { margin-bottom: 1rem; }
        .text-center { text-align: center; }
        h2 { font-size: 15px; font-weight: 600; margin: 0; }
        .emp-name { font-size: 13px; text-decoration: underline; font-weight: 500; }
        .total-row td { border-top: 2px solid #888; font-weight: 600; }
        .sub-text { color: #888; font-size: 11px; }
      </style></head><body>
    `);
    const employees = Object.keys(groupedData).sort();
    for (const empName of employees) {
      const entries = groupedData[empName];
      const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);
      printWindow.document.write(`
        <div class="mb-12">
          <div class="text-center mb-2"><h2>Servpro - Production Time Card</h2></div>
          <div class="mb-4"><span class="emp-name">${empName}</span></div>
          <table>
            <thead><tr>
              <th>Date</th><th>Customer</th><th>Project</th><th>Tech/Temps</th><th>Vehicle</th><th class="text-right">Hours Charged</th>
            </tr></thead><tbody>
      `);
      for (const entry of entries) {
        printWindow.document.write(`
          <tr>
            <td style="white-space:nowrap">${entry.date || ''}</td>
            <td>${entry.customer || ''}</td>
            <td>${entry.project || ''}${entry.project_address ? '<div class="sub-text">' + entry.project_address + '</div>' : ''}</td>
            <td>${entry.tech_temps || ''}</td>
            <td>${entry.vehicle || ''}</td>
            <td class="text-right">${entry.hours || 0}</td>
          </tr>
        `);
      }
      printWindow.document.write(`
            <tr class="total-row">
              <td colspan="5" class="text-right">Total Hours</td>
              <td class="text-right">${totalHours}</td>
            </tr>
          </tbody></table>
        </div>
      `);
    }
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  // Form view (before print)
  if (!groupedData) {
    return (
      <Layout>
        <div className="p-4 lg:p-8">
          <div className="mb-6">
            <h2 className="text-base lg:text-lg text-[#5B8DB8] font-light">Timeclock</h2>
            <h1 className="text-2xl lg:text-3xl text-[#5B8DB8] font-light italic">Print Time Cards</h1>
          </div>
          <div className="bg-white rounded shadow p-8 max-w-md">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <label className="text-red-500 text-sm w-32 text-right">Time From:</label>
                <Popover open={calFromOpen} onOpenChange={setCalFromOpen}>
                  <PopoverTrigger asChild>
                    <input
                      type="text"
                      value={timeFrom}
                      readOnly
                      onClick={() => setCalFromOpen(true)}
                      className="border border-gray-300 rounded px-3 py-2 flex-1 cursor-pointer focus:outline-none focus:border-[#6BADE0]"
                      data-testid="time-from-input"
                    />
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={timeFrom ? new Date(timeFrom) : undefined} onSelect={(d) => { if (d) setTimeFrom(format(d, 'M/dd/yyyy')); setCalFromOpen(false); }} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-4">
                <label className="text-red-500 text-sm w-32 text-right">Time To:</label>
                <Popover open={calToOpen} onOpenChange={setCalToOpen}>
                  <PopoverTrigger asChild>
                    <input
                      type="text"
                      value={timeTo}
                      readOnly
                      onClick={() => setCalToOpen(true)}
                      className="border border-gray-300 rounded px-3 py-2 flex-1 cursor-pointer focus:outline-none focus:border-[#6BADE0]"
                      data-testid="time-to-input"
                    />
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={timeTo ? new Date(timeTo) : undefined} onSelect={(d) => { if (d) setTimeTo(format(d, 'M/dd/yyyy')); setCalToOpen(false); }} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex justify-center pt-4">
                <button
                  onClick={handlePrint}
                  disabled={loading}
                  className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-8 py-3 rounded text-lg font-medium transition-colors disabled:opacity-50"
                  data-testid="print-btn"
                >
                  {loading ? 'Loading...' : 'Print'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Printable time cards view
  const employees = Object.keys(groupedData).sort();

  return (
    <div className="bg-white min-h-screen">
      {/* Print controls - hidden during actual print */}
      <div className="print:hidden flex items-center gap-4 p-4 bg-gray-100 border-b">
        <button
          onClick={() => setGroupedData(null)}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm transition-colors"
          data-testid="back-btn"
        >
          Back
        </button>
        <button
          onClick={handleBrowserPrint}
          className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded text-sm font-medium transition-colors"
          data-testid="browser-print-btn"
        >
          Print Page
        </button>
        <span className="text-sm text-gray-500">
          Time Cards: {timeFrom} to {timeTo}
        </span>
      </div>

      <div className="p-8 max-w-4xl mx-auto" data-testid="print-view">
        {employees.map((empName) => {
          const entries = groupedData[empName];
          const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);

          return (
            <div key={empName} className="mb-12 break-inside-avoid" data-testid={`timecard-${empName.replace(/[^a-zA-Z]/g, '-')}`}>
              {/* Header */}
              <div className="text-center mb-2">
                <h2 className="text-base font-semibold text-gray-800">Servpro - Production Time Card</h2>
              </div>

              {/* Employee name */}
              <div className="mb-4">
                <span className="text-sm text-gray-700 underline font-medium">{empName}</span>
              </div>

              {/* Time card table */}
              <table className="w-full text-sm border-collapse" data-testid={`timecard-table-${empName.replace(/[^a-zA-Z]/g, '-')}`}>
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-2 px-2 text-gray-600 font-semibold text-xs">Date</th>
                    <th className="text-left py-2 px-2 text-gray-600 font-semibold text-xs">Customer</th>
                    <th className="text-left py-2 px-2 text-gray-600 font-semibold text-xs">Project</th>
                    <th className="text-left py-2 px-2 text-gray-600 font-semibold text-xs">Tech/Temps</th>
                    <th className="text-left py-2 px-2 text-gray-600 font-semibold text-xs">Vehicle</th>
                    <th className="text-right py-2 px-2 text-gray-600 font-semibold text-xs">Hours Charged</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, idx) => (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="py-2 px-2 text-gray-700 text-xs whitespace-nowrap">{entry.date}</td>
                      <td className="py-2 px-2 text-gray-700 text-xs">{entry.customer}</td>
                      <td className="py-2 px-2 text-xs">
                        <div className="text-gray-700">{entry.project}</div>
                        {entry.project_address && <div className="text-gray-500">{entry.project_address}</div>}
                      </td>
                      <td className="py-2 px-2 text-gray-700 text-xs">{entry.tech_temps}</td>
                      <td className="py-2 px-2 text-gray-700 text-xs">{entry.vehicle}</td>
                      <td className="py-2 px-2 text-gray-700 text-xs text-right">{entry.hours}</td>
                    </tr>
                  ))}
                  {/* Total row */}
                  <tr className="border-t-2 border-gray-400">
                    <td colSpan={5} className="py-2 px-2 text-right text-xs font-semibold text-gray-700">Total Hours</td>
                    <td className="py-2 px-2 text-right text-xs font-semibold text-gray-700">{totalHours}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PrintTimeCards;
